import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  ViewContainerRef,
} from '@angular/core';
import { PaginatedData } from '@interfaces/response.interface';
import { NgSelectConfig } from '@ng-select/ng-select';
import {
  BehaviorSubject,
  debounceTime,
  distinctUntilChanged,
  finalize,
  Observable,
  pipe,
  Subject,
  tap,
  UnaryFunction,
} from 'rxjs';

import { SelectComponent } from './select.component';

export interface ServerSideSearchSelectDataSource<Service, RecordT, QueryParamsT> {
  dataService: Service;
  getRecord?: (...args: any[]) => Observable<RecordT>;
  recordArgs?: any[];
  recordOperations?: UnaryFunction<Observable<RecordT>, Observable<RecordT>>;
  getRecords: (...args: any[]) => Observable<PaginatedData<RecordT>>;
  recordsArgs?: any[];
  queryParams?: QueryParamsT;
  recordsOperations?: UnaryFunction<Observable<PaginatedData<RecordT>>, Observable<PaginatedData<RecordT>>>;
  searchField: keyof QueryParamsT;
}

@Component({
  selector: 'app-sss-select',
  templateUrl: './server-side-search-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ServerSideSearchSelectComponent extends SelectComponent implements OnInit, AfterViewInit {
  @Input() dataSource?: ServerSideSearchSelectDataSource<
    object,
    (...args: unknown[]) => Observable<Record<string, unknown>>,
    (...args: unknown[]) => Observable<PaginatedData<Record<string, any>>>
  >;
  @Input() pageSize = 100;
  total: number | null = null;
  isLoading = new BehaviorSubject(false);
  readonly search = new Subject<string>();

  private page = 0;
  private searchPhrase = '';

  constructor(
    protected override ngSelectConfig: NgSelectConfig,
    protected override readonly cdr: ChangeDetectorRef,
    protected override readonly viewContainerRef: ViewContainerRef,
  ) {
    super(ngSelectConfig, cdr, viewContainerRef);
    this.ngSelectConfig.typeToSearchText = $localize`Pisz, by wyszukać...`;
  }

  get allDataFetched(): boolean {
    return this.total ? this.data?.length >= this.total : false;
  }

  ngOnInit(): void {
    this.subscription.add(
      this.search
        .pipe(
          distinctUntilChanged(),
          debounceTime(300),
          tap((v) => {
            this.searchPhrase = v;
            this.page = 0;
            this.total = null;
          }),
        )
        .subscribe(() => {
          if (!this.allDataFetched && this.select.isOpen) {
            this.getRecords();
          }
        }),
    );

    if (this.reactiveControl.value) {
      if (this.dataSource.getRecord) {
        this.getCurrentRecord();
      } else {
        this.getRecords();
      }
    }
  }

  onOpen(): void {
    if (this.reactiveControl.enabled && !this.isLoading.getValue() && !this.allDataFetched) {
      this.getRecords();
    }
  }

  onScrollToEnd(): void {
    if (this.isLoading.getValue() || (!!this.data && this.allDataFetched)) {
      return;
    }
    this.page++;
    this.getRecords();
  }

  private getCurrentRecord(): void {
    this.isLoading.next(true);
    const getEl = (el: any): void => {
      this.dataSource.getRecord
        .bind(this.dataSource.dataService, ...(this.dataSource.recordArgs ?? [el]))()
        .pipe(
          this.dataSource.recordOperations ?? pipe(),
          finalize(() => this.isLoading.next(false)),
        )
        .subscribe((v: Record<string, unknown>) => {
          if (this.data) {
            this.data = [...new Set([...this.data, v])];
          } else {
            this.data = [v];
          }
          this.cdr.detectChanges();
        });
    };

    if (this.reactiveControl.value instanceof Array && this.isMultiple) {
      this.reactiveControl.value.forEach((el: any) => {
        if (el instanceof String) {
          getEl(el);
        } else if (el instanceof Object) {
          getEl(el.id);
        }
      });
    } else {
      getEl(this.reactiveControl.value);
    }
  }

  private getRecords() {
    this.isLoading.next(true);
    const queryParams = {
      page: this.page,
      pageSize: this.pageSize,
      [this.dataSource.searchField]: this.searchPhrase ?? '',
      ...this.dataSource.queryParams,
    };

    (
      (this.dataSource.recordsArgs
        ? this.dataSource.getRecords.bind(this.dataSource.dataService, ...this.dataSource.recordsArgs, queryParams)
        : this.dataSource.getRecords.bind(this.dataSource.dataService, queryParams))() as Observable<
        PaginatedData<unknown>
      >
    )
      .pipe(
        this.dataSource.recordsOperations ?? pipe(),
        finalize(() => this.isLoading.next(false)),
      )
      .subscribe((v: PaginatedData<Record<string, any>>) => {
        this.data = this.page === 0 ? v.data : [...new Set([...this.data, ...v.data])];
        this.total = v.total;
        this.cdr.detectChanges();
      });
  }
}
